import { ref, readonly } from 'vue';
import authService, { type User, type LoginData, type RegisterData, type VerifyEmailData } from '@/services/authService';

// State
const isLoggedIn = ref(false);
const user = ref<User | null>(null);
const token = ref<string | null>(localStorage.getItem('token'));

// Private actions
const setAuthData = (userData: User, authToken: string) => {
  console.log('setAuthData called with token:', authToken);
  user.value = userData;
  isLoggedIn.value = true;
  token.value = authToken;
  localStorage.setItem('token', authToken);
  // For simplicity, we can also store user data, but the token is the source of truth
  localStorage.setItem('user', JSON.stringify(userData));
  console.log('setAuthData completed - isLoggedIn:', isLoggedIn.value);
};

const clearAuthData = () => {
  user.value = null;
  isLoggedIn.value = false;
  token.value = null;
  localStorage.removeItem('token');
  localStorage.removeItem('user');
};

// Public actions
const login = async (credentials: LoginData) => {
  try {
    const response = await authService.login(credentials);
    console.log('Full login API response:', response.data);

    // Handle the actual API response structure
    const { user: userData, access_token } = response.data.data;

    console.log('Login user data:', userData);
    console.log('Login email verified at:', userData.email_verified_at);

    // Check if user email is verified
    if (userData.email_verified_at === null) {
      // Save token temporarily for verification process, but don't set as logged in
      token.value = access_token;
      localStorage.setItem('token', access_token);
      user.value = userData;
      // Don't set isLoggedIn = true yet, return user data for verification step
      return Promise.resolve({ user: userData, needsVerification: true });
    }

    setAuthData(userData, access_token);
    return Promise.resolve({ user: userData, needsVerification: false });
  } catch (error) {
    clearAuthData();
    return Promise.reject(error);
  }
};

const register = async (data: RegisterData) => {
  try {
    const response = await authService.register(data);
    console.log('REGISTER - Full API response:', response.data);

    // Handle the actual API response structure
    const { user: userData, access_token } = response.data.data;

    console.log('REGISTER - User data:', userData);
    console.log('REGISTER - Email verified at:', userData.email_verified_at);

    // Check if user email is verified
    if (userData.email_verified_at === null) {
      console.log('REGISTER - Email not verified, saving temp token:', access_token);
      // Save token temporarily for verification process, but don't set as logged in
      token.value = access_token;
      localStorage.setItem('token', access_token);
      user.value = userData;
      console.log('REGISTER - Token saved to localStorage');
      // Don't set isLoggedIn = true yet, return user data for verification step
      return Promise.resolve({ user: userData, needsVerification: true });
    }

    setAuthData(userData, access_token);
    return Promise.resolve({ user: userData, needsVerification: false });
  } catch (error) {
    clearAuthData();
    return Promise.reject(error);
  }
};

const logout = async () => {
  try {
    // Call API to invalidate token on the server
    await authService.logout();
  } catch (error) {
    console.error("Logout failed on server, clearing client-side session anyway.", error);
  } finally {
    // Always clear client-side data
    clearAuthData();
  }
};

const verifyEmail = async (data: VerifyEmailData) => {
  try {
    const response = await authService.verifyEmail(data);
    console.log('VERIFY - Email verification successful:', response.data);

    // Verification API only returns success, no user data
    // We need to fetch updated user data using the existing token
    console.log('VERIFY - Fetching updated user data...');
    const userResponse = await authService.getUser();
    const userData = userResponse.data.user;

    console.log('VERIFY - Updated user data:', userData);
    console.log('VERIFY - Using existing token:', token.value);

    // Complete the login process with existing token and updated user data
    setAuthData(userData, token.value!);
    console.log('VERIFY - Login completed, isLoggedIn should be true');
    return Promise.resolve(userData);
  } catch (error) {
    return Promise.reject(error);
  }
};

const resendVerification = async (email: string) => {
  try {
    const response = await authService.resendVerification(email);
    return Promise.resolve(response.data);
  } catch (error) {
    return Promise.reject(error);
  }
};

const checkAuth = async () => {
  const localStorageToken = localStorage.getItem('token');

  // Fix token sync issue - if localStorage has token but token.value is null
  if (localStorageToken && !token.value) {
    token.value = localStorageToken;
  }

  if (token.value) {
    try {
      // Token exists, verify it by fetching user data
      const response = await authService.getUser();
      const userData = response.data.user;
      // If successful, we are authenticated
      isLoggedIn.value = true;
      user.value = userData;
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (error) {
      // Token is invalid or expired
      console.error("Token verification failed", error);
      clearAuthData();
    }
  } else {
    // No token, ensure we are logged out
    clearAuthData();
  }
};

// Export the store parts
export const authStore = {
  isLoggedIn: readonly(isLoggedIn),
  user: readonly(user),
  login,
  register,
  verifyEmail,
  resendVerification,
  logout,
  checkAuth,
};